export const BATCH_REPROCESS_TTL_MS = 30 * 60 * 1000;
export const DEFAULT_BATCH_REPROCESS_PATH = "/api/transcriber";

export type BatchReprocessState =
  | "unsupported"
  | "waiting_for_stop"
  | "unconfigured"
  | "ready"
  | "running"
  | "expired"
  | "error";

export type BatchReprocessErrorCode =
  | "UNSUPPORTED_PROVIDER"
  | "REPROCESS_IN_PROGRESS"
  | "SESSION_NOT_STOPPED"
  | "NOT_CONFIGURED"
  | "AUDIO_NOT_AVAILABLE"
  | "AUDIO_EXPIRED"
  | "HTTP_ERROR"
  | "INVALID_RESPONSE"
  | "CORS_BLOCKED";

export interface BatchReprocessStatus {
  state: BatchReprocessState;
  supported: boolean;
  configured: boolean;
  available: boolean;
  expiresAt: number | null;
  remainingMs: number | null;
  lastStartedAt: number | null;
  lastCompletedAt: number | null;
  lastError: BatchReprocessError | null;
}

export interface BatchReprocessConfig<TPayload = unknown> {
  endpoint?: string;
  parseResponse?: (response: Response) => Promise<TPayload>;
}

export interface ResolvedWhisperConnectionContext {
  realtimeUrl: string;
  batchBaseUrl: string;
  batchDefaultUrl: string;
  authToken?: string;
  externalId?: string;
  transcriptionLanguage?: string;
  translationLanguage?: string;
  headers?: Record<string, string>;
}

interface BatchReprocessErrorOptions {
  cause?: unknown;
  message?: string;
  status?: number | null;
}

const DEFAULT_ERROR_MESSAGES: Record<BatchReprocessErrorCode, string> = {
  UNSUPPORTED_PROVIDER:
    "Audio reprocessing is only supported for durable Whisper sessions.",
  REPROCESS_IN_PROGRESS:
    "Audio reprocessing is already running for this session.",
  SESSION_NOT_STOPPED:
    "Audio reprocessing is only available after the session has ended.",
  NOT_CONFIGURED: "Batch reprocess endpoint is not configured.",
  AUDIO_NOT_AVAILABLE:
    "Stored consultation audio is not available for batch reprocessing.",
  AUDIO_EXPIRED:
    "Stored consultation audio is no longer available for batch reprocessing.",
  HTTP_ERROR: "Batch reprocess request failed.",
  INVALID_RESPONSE: "Batch reprocess returned an invalid payload.",
  CORS_BLOCKED:
    "Batch reprocess request was blocked before the response could be read.",
};

export class BatchReprocessError extends Error {
  public readonly code: BatchReprocessErrorCode;
  public readonly cause: unknown;
  public readonly status: number | null;

  constructor(
    code: BatchReprocessErrorCode,
    options: BatchReprocessErrorOptions = {}
  ) {
    super(options.message ?? DEFAULT_ERROR_MESSAGES[code]);
    this.name = "BatchReprocessError";
    this.code = code;
    this.status = options.status ?? null;
    this.cause = options.cause ?? null;
  }
}

interface BatchReprocessStatusInput {
  state: BatchReprocessState;
  available?: boolean;
  configured?: boolean;
  expiresAt?: number | null;
  lastCompletedAt?: number | null;
  lastError?: BatchReprocessError | null;
  lastStartedAt?: number | null;
  now?: number;
  supported?: boolean;
}

export const createBatchReprocessStatus = (
  input: BatchReprocessStatusInput
): BatchReprocessStatus => {
  const supported = input.supported ?? input.state !== "unsupported";
  const configured =
    input.configured ?? (supported && input.state !== "unconfigured");
  const expiresAt = input.expiresAt ?? null;
  const remainingMs =
    expiresAt === null ? null : Math.max(0, expiresAt - (input.now ?? Date.now()));

  return {
    state: input.state,
    supported,
    configured,
    available: input.available ?? input.state === "ready",
    expiresAt,
    remainingMs,
    lastStartedAt: input.lastStartedAt ?? null,
    lastCompletedAt: input.lastCompletedAt ?? null,
    lastError: input.lastError ?? null,
  };
};

export const refreshBatchReprocessStatus = (
  status: BatchReprocessStatus,
  now = Date.now()
): BatchReprocessStatus => {
  const shouldExpire =
    status.expiresAt !== null &&
    status.expiresAt <= now &&
    (status.state === "ready" ||
      status.state === "running" ||
      status.state === "error" ||
      status.state === "unconfigured");

  return createBatchReprocessStatus({
    ...status,
    state: shouldExpire ? "expired" : status.state,
    now,
  });
};

export const getBatchReprocessRemainingTime = (
  status: BatchReprocessStatus,
  now = Date.now()
): number | null => refreshBatchReprocessStatus(status, now).remainingMs;

export const createUnsupportedBatchReprocessStatus = (): BatchReprocessStatus =>
  createBatchReprocessStatus({
    state: "unsupported",
    supported: false,
    configured: false,
    available: false,
  });
